import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useBreakpoints } from 'hooks';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { AuthModal } from 'interfaces/auth';
import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { TOOLTIP_MODE } from 'components/_basic/TooltipComponent/constants';
import { useAuthModalsContext } from 'components/AuthModals';
import { useHeaderLogic } from 'components/Header/HeaderLogicProvider';

import { IPDFOrShareTooltip } from './types';

import styles from './PDFOrShareTooltip.module.scss';

export const PDFOrShareTooltip = ({ children, isPdfTooltip = true, isShareTooltip = false, leaveDelay = 200 }: IPDFOrShareTooltip) => {
  const { isAuthenticated } = useAuthenticated();
  const { t } = useTranslation();
  const { setAuthModal, setIsModalOpened } = useAuthModalsContext();
  const { isMobile } = useBreakpoints();

  const logInSignUpHandler = (type: AuthModal) => {
    setIsModalOpened(true);
    setAuthModal(type);
  };
  const { handleClickUpgradePlan } = useHeaderLogic();

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  let node = null;

  if (!isAuthenticated) {
    node = (
      <Box>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={'16px'} className={styles.title}>
          {isMobile ? t('pleaseSignUp') : t('pleaseLogin')}
        </Typography>
        <Typography variant="subtitle2" className={styles.text}>
          {isPdfTooltip
            ? isMobile
              ? t('signupToExportTheProject')
              : t('loginToExportPDF')
            : isMobile
              ? t('signupToShareTheProject')
              : t('loginToShare')}
        </Typography>
        <Box className={styles.buttonWrapper}>
          <Button variant="text" className={styles.button} onClick={() => logInSignUpHandler(AuthModal.LOGIN)}>
            {t('logIn')}
          </Button>
          <Button variant="text" className={styles.button} onClick={() => logInSignUpHandler(AuthModal.SIGN_UP)}>
            {t('signUp')}
          </Button>
        </Box>
      </Box>
    );
  } else if (!isStandardOrLightTariff && isPdfTooltip) {
    node = (
      <Box>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={'16px'} className={styles.title}>
          {t('upgradeYourPlan')}
        </Typography>
        <Typography variant="subtitle2" className={styles.text}>
          {t('upgradeToExportPDF')}
        </Typography>
        <Button size="small" color="secondary" variant="outlined" onClick={handleClickUpgradePlan}>
          {t('upgradePlan')}
        </Button>
      </Box>
    );
  } else if (!isStandardOrLightTariff && isShareTooltip) {
    node = (
      <Box>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={'16px'} className={styles.title}>
          {t('upgradeYourPlan')}
        </Typography>
        <Typography variant="subtitle2" className={styles.text}>
          {t('upgradeToShareProject')}
        </Typography>
        <Button size="small" color="secondary" variant="outlined" onClick={handleClickUpgradePlan}>
          {t('upgradePlan')}
        </Button>
      </Box>
    );
  }
  if (!node) {
    return children;
  }

  return (
    <TooltipComponent
      leaveDelay={leaveDelay}
      title={node}
      isTooltipHidden={isStandardOrLightTariff}
      mode={TOOLTIP_MODE.WHITE}
      isTitleIsNode
      trigger={isMobile ? 'click' : 'hover'}
    >
      {children}
    </TooltipComponent>
  );
};
